<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Under_the_Hood/FreTool_Interfaces';
    let nextLink= '/Tutorial';
    
</script>
<PrevNextSection {prevLink} {nextLink} />
<SectionComponent tag="h1" 
  id="freon-terminology-1"
  bind:intersecting={visibleById["freon-terminology-1"]}
  onIntersectChange={(visible) => setVisible("freon-terminology-1", visible)}
>Freon Terminology</SectionComponent>
<SectionComponent tag="h2" 
  id="a---c-2"
  bind:intersecting={visibleById["a---c-2"]}
  onIntersectChange={(visible) => setVisible("a---c-2", visible)}
>A - C</SectionComponent>
<table>
<thead>
<tr>
<th>Phrase</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td>Abstract concept</td>
<td>A concept that cannot be instantiated directly; other concepts inherit from it.</td>
</tr>
<tr>
<td>Abstract Syntax Tree (AST)</td>
<td>The internal tree structure of a model, where each node represents an instance of a language concept. It is the core model manipulated directly by users of the Freon editor instead of plain text.</td>
</tr>
<tr>
<td>Action</td>
<td>Certain behavior of the editor, for instance, the reaction to a key stroke.</td>
</tr>
<tr>
<td>Alternative namespace</td>
<td>A statement in the scoper definition that removes the names from the parent namespace, and replaces them by names of another namespace.</td>
</tr>
<tr>
<td>Alternative Nodes</td>
<td>Declared nodes from namespaces that have an alternative relationship with the current namespace, defined in the alternatives block.</td>
</tr>
<tr>
<td><code>alternatives</code> (keyword)</td>
<td>Defines replacement or non-hierarchical visibility relationships between namespaces.</td>
</tr>
<tr>
<td>Anytype Rule</td>
<td>A general validation rule that applies to all concepts.</td>
</tr>
<tr>
<td>AST</td>
<td>See Abstract Syntax Tree (AST).</td>
</tr>
<tr>
<td>.ast file</td>
<td>File containing the definition of the metamodel of the language. The extension must be ‘.ast’.</td>
</tr>
<tr>
<td>AST.change()</td>
<td>A helper method from @freon4dsl/core to ensure all model mutations are MobX-reactive.</td>
</tr>
<tr>
<td>AST-Based Interpretation</td>
<td>Evaluation performed directly on the AST, without parsing or lexing steps, thanks to Freon’s projectional editor.</td>
</tr>
<tr>
<td>Balancing expressions</td>
<td>Automatic restructuring of binary expression trees in Freon so that operator precedence is reflected correctly after editing.</td>
</tr>
<tr>
<td>Binary expression</td>
<td>A subtype of Expression that defines an expression with a left and right operand, combined using an operator such as +, *, AND, or &gt;.</td>
</tr>
<tr>
<td>Boolean type</td>
<td>A primitive type with two values: true or false.</td>
</tr>
<tr>
<td>Box</td>
<td>A TypeScript class that represents a rectangle on the editor screen.</td>
</tr>
<tr>
<td>Box API</td>
<td>Key editor box methods such as findParam(), setFocus(), and refreshComponent(). Used by external components and custom projections for runtime interaction with the editor.</td>
</tr>
<tr>
<td>Box Provider</td>
<td>A generated class that produces boxes for specific AST concepts in the Freon editor.</td>
</tr>
<tr>
<td>Box Role</td>
<td>An identifier assigned to a box that links it to specific editor behaviors or actions. Box roles connect visual elements in a projection to the logic that handles user interactions.</td>
</tr>
<tr>
<td>Box Tree / box model</td>
<td>The hierarchy of boxes used by the Freon editor to represent layout and structure of a model on screen.</td>
</tr>
<tr>
<td>Caret Position</td>
<td>The current insertion point in the editor, managed by the FreCaret interface.</td>
</tr>
<tr>
<td>Concept</td>
<td>The primary building block of a Freon metamodel, similar to a class in UML.</td>
</tr>
<tr>
<td>Concrete Syntax Tree (CST)</td>
<td>A tree representing how a model appears visually; in Freon, this corresponds to the Box Model.</td>
</tr>
<tr>
<td><code>conformsto</code> (keyword)</td>
<td>A predefined function in the typer, that defines that a certain type conforms to another type. The function can also be used in the validator.</td>
</tr>
<tr>
<td>Container Descriptor (piContainer)</td>
<td>Metadata linking an element to its parent container in the AST hierarchy.</td>
</tr>
<tr>
<td>CST</td>
<td>See Concrete Syntax Tree (CST).</td>
</tr>
<tr>
<td>Custom Action</td>
<td>A user-defined editor action created with FreCustomAction.create().</td>
</tr>
<tr>
<td>Custom Box Definitions</td>
<td>User-defined layouts or components replacing generated box structures.</td>
</tr>
<tr>
<td>Custom Message Rule</td>
<td>Definition of user-defined error messages and severity levels (message, severity) used during validation.</td>
</tr>
<tr>
<td>Custom Projection</td>
<td>A user-defined method that returns a box for a given concept, registered in nodeTypeToBoxMethod.</td>
</tr>
<tr>
<td>Custom Scoper</td>
<td>A user-defined replacement for, or augmentation to, the generated scoper, currently applied globally rather than per concept.</td>
</tr>
<tr>
<td>Custom Typer</td>
<td>A user-defined replacement for, or augmentation to, the generated typer, applied per concept.</td>
</tr>
<tr>
<td>Customization Level</td>
<td>The layer where handwritten TypeScript code extends or replaces generated behavior in Freon.</td>
</tr>
</tbody>
</table>
<SectionComponent tag="h2" 
  id="d---e-3"
  bind:intersecting={visibleById["d---e-3"]}
  onIntersectChange={(visible) => setVisible("d---e-3", visible)}
>D - E</SectionComponent>
<table>
<thead>
<tr>
<th>Phrase</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td>Declared Nodes</td>
<td>All named nodes (with a name: identifier property) within a namespace’s AST subtree, including nodes that define other namespaces, but excluding the declared nodes from those namespaces.</td>
</tr>
<tr>
<td>Default Validation Rules</td>
<td>Automatically enforced checks such as required properties, valid references, and non-empty lists.</td>
</tr>
<tr>
<td>Document Object Model (DOM)</td>
<td>The browser’s structure of HTML elements; Freon’s rendered boxes eventually map to DOM nodes.</td>
</tr>
<tr>
<td>Domain-Specific Language (DSL)</td>
<td>A programming or modeling language tailored to a specific problem domain, providing constructs and abstractions closely aligned with domain concepts rather than general-purpose computation. In Freon, DSLs are designed, edited, and executed using generated tools that combine structural definitions (metamodels), projections, and validation rules.</td>
</tr>
<tr>
<td>Domain-Specific Runtime Class</td>
<td>A subclass of RtObject that models domain-specific runtime behavior.</td>
</tr>
<tr>
<td>DSL</td>
<td>See Domain-Specific Language</td>
</tr>
<tr>
<td>.edit file</td>
<td>File containing the definition of the editors, or projection sets of the language. The extension must be ‘.edit’.</td>
</tr>
<tr>
<td>Editor</td>
<td>A loosely used term to refer to the runtime tool in the browser, or to a certain projection set.</td>
</tr>
<tr>
<td>Editor definition (.edit)</td>
<td>A file describing how concepts and their properties are projected in the Freon editor, thus defining a projection set.</td>
</tr>
<tr>
<td>Editor Framework</td>
<td>The set of Freon subsystems that manage projections, actions, caret movement, and rendering.</td>
</tr>
<tr>
<td>Embedding Freon</td>
<td>Integrating the Freon editor into a custom web application, typically using Svelte components.</td>
</tr>
<tr>
<td>Enumeration</td>
<td>See limited concept.</td>
</tr>
<tr>
<td><code>equalsto</code> (keyword)</td>
<td>A predefined function in the typer, that defines that a certain type is considered to be equal to another type. The function can also be used in the validator.</td>
</tr>
<tr>
<td>Error Severity</td>
<td>Classification of validation messages (e.g., error, warning, hint, info).</td>
</tr>
<tr>
<td>Evaluation</td>
<td>The process of computing the value associated with an AST node during interpretation.</td>
</tr>
<tr>
<td>Evaluation Function</td>
<td>A generated or user-defined method that determines the meaning or output of a specific AST node.</td>
</tr>
<tr>
<td>Evaluation Override</td>
<td>The act of redefining a generated evaluation function to perform actual interpretation logic.</td>
</tr>
<tr>
<td>Expression (or Expression Concept)</td>
<td>A subtype of Concept that defines a construct that produces a value.</td>
</tr>
<tr>
<td>External Component</td>
<td>A Svelte or Svelte-wrapped component embedded inside a Freon projection.</td>
</tr>
</tbody>
</table>
<SectionComponent tag="h2" 
  id="f---h-4"
  bind:intersecting={visibleById["f---h-4"]}
  onIntersectChange={(visible) => setVisible("f---h-4", visible)}
>F - H</SectionComponent>
<table>
<thead>
<tr>
<th>Phrase</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td>Fragment</td>
<td>A part of a projection definition.</td>
</tr>
<tr>
<td>FreAction</td>
<td>Defines an executable editor operation such as inserting, deleting, or modifying nodes.</td>
</tr>
<tr>
<td>FreBehavior</td>
<td>Encapsulates user interaction logic that links triggers to editor actions.</td>
</tr>
<tr>
<td>FreCaret</td>
<td>Interface representing the caret’s current position, movement, and focus behavior.</td>
</tr>
<tr>
<td>FreComponentProps</td>
<td>The properties passed to an external component, containing editor and box references.</td>
</tr>
<tr>
<td>FreCustomAction</td>
<td>User-defined action class used to add or override default editor behavior.</td>
</tr>
<tr>
<td>FreEditor</td>
<td>The main runtime editor interface controlling projections, boxes, and user interactions.</td>
</tr>
<tr>
<td>FreEnvironment</td>
<td>Central registry providing access to language definitions, scopers, typers, and validators.</td>
</tr>
<tr>
<td>FreError Interface</td>
<td>Interface defining issues or diagnostics produced during validation or typing.</td>
</tr>
<tr>
<td>FreExpression Interface</td>
<td>Base interface for all expression elements in the Freon model.</td>
</tr>
<tr>
<td>FreNamedNode Interface</td>
<td>Interface that extends FreNode with a name property used in references and scoping.</td>
</tr>
<tr>
<td>FreNode Interface</td>
<td>The base interface for all Freon model elements, providing identity and structural information.</td>
</tr>
<tr>
<td>FreNodeReference</td>
<td>A Freon class that represents name-based references, ensuring models remain trees instead of graphs.</td>
</tr>
<tr>
<td>Freon</td>
<td>The Freon framework for building DSL editors for the web. The name ‘Freon’ comes from the Frisian word for ‘friend’.</td>
</tr>
<tr>
<td><code>freon all</code></td>
<td>Command that generates all dependent components in the correct order, ensuring compilable and consistent output.</td>
</tr>
<tr>
<td><code>freon ast-it</code></td>
<td>Command that generates code for all concepts, units etc. from .lang definition files.</td>
</tr>
<tr>
<td><code>freon clean-it</code></td>
<td>Command that removes all generated TypeScript code for your language.</td>
</tr>
<tr>
<td><code>freon diagram-it</code></td>
<td>Command that generates UML diagrams from .ast definition files.</td>
</tr>
<tr>
<td><code>freon edit-it</code></td>
<td>Command that generates projection code from .edit definition files.</td>
</tr>
<tr>
<td>Freon Editor</td>
<td>The projectional graphical editor generated from a Freon DSL specification.</td>
</tr>
<tr>
<td>Freon interpreter framework</td>
<td>A Freon runtime framework that allows execution or simulation of DSL models.</td>
</tr>
<tr>
<td><code>freon interpret-it</code></td>
<td>Command that generates interpreter code for your language.</td>
</tr>
<tr>
<td>Freon Language Workbench</td>
<td>The Freon platform for defining, generating, and executing web-based DSL editors.</td>
</tr>
<tr>
<td><code>freon parse-it</code></td>
<td>Command that generates parser code from .edit definition files.</td>
</tr>
<tr>
<td><code>freon scope-it</code></td>
<td>Command that generates scoping code from .scope definition files.</td>
</tr>
<tr>
<td><code>freon type-it</code></td>
<td>Command that generates typing code from .type definition files.</td>
</tr>
<tr>
<td><code>freon validate-it</code></td>
<td>Command that generates validation code from .valid definition files.</td>
</tr>
<tr>
<td>FrePostAction</td>
<td>Action executed after a primary action, often for cleanup or UI refresh.</td>
</tr>
<tr>
<td>FreProjection Interface</td>
<td>Interface defining how a language concept is visually projected in the editor.</td>
</tr>
<tr>
<td>FreProjectionHandler</td>
<td>A Freon runtime class that manages projection rendering and links between AST nodes and visual boxes.</td>
</tr>
<tr>
<td>FreReader</td>
<td>Component that parses models from text or serialized data into AST structures.</td>
</tr>
<tr>
<td>FreReader Interface</td>
<td>Interface defining how models are parsed from text input into Freon’s AST representation.</td>
</tr>
<tr>
<td>FreScoper</td>
<td>Component responsible for resolving references and managing name visibility in the model.</td>
</tr>
<tr>
<td>FreScoper Interface</td>
<td>Interface defining the logic for resolving references and visible names within a Freon model.</td>
</tr>
<tr>
<td>FreStdlib</td>
<td>Component that holds the standard library of predefined elements and limited concepts available in a language.</td>
</tr>
<tr>
<td>FreStdlib Interface</td>
<td>Interface defining all predefined instances and limited concepts for a given language in Freon.</td>
</tr>
<tr>
<td>Fre-tools</td>
<td>The modular components generated by Freon, such as the editor, scoper, typer, validator, parser, and standard library.</td>
</tr>
<tr>
<td>FreTriggerType</td>
<td>Enumeration defining input types (e.g., key, mouse) that can trigger editor actions.</td>
</tr>
<tr>
<td>FreTriggerUse</td>
<td>Enumeration describing how triggers are applied (e.g., pressed, released, repeated).</td>
</tr>
<tr>
<td>FreType Interface</td>
<td>Interface implemented by all type concepts in Freon.</td>
</tr>
<tr>
<td>FreTyper</td>
<td>Component for inferring and checking the types of model elements.</td>
</tr>
<tr>
<td>FreTyper Interface</td>
<td>Interface defining the logic for type inference, type equality, and conformance checks.</td>
</tr>
<tr>
<td>FreTyperPart</td>
<td>A modular part of the typer that implements type inference for a subset of concepts.</td>
</tr>
<tr>
<td>FreUndoManager</td>
<td>Component that handles undo and redo operations for model edits.</td>
</tr>
<tr>
<td>FreValidator</td>
<td>Component for validating models to ensure semantic correctness.</td>
</tr>
<tr>
<td>FreValidator Interface</td>
<td>Interface defining how models are validated to ensure semantic correctness.</td>
</tr>
<tr>
<td>FreWriter</td>
<td>Component that serializes models back to text or persistent form.</td>
</tr>
<tr>
<td>FreWriter Interface</td>
<td>Interface defining how models are serialized or exported from Freon to text or files.</td>
</tr>
<tr>
<td>Global section</td>
<td>The .edit file section listing available external components in a language, and settings to be applied globally in the editor.</td>
</tr>
<tr>
<td>GradeScore</td>
<td>A concept representing a grading rule composed of a grade and an expression.</td>
</tr>
<tr>
<td>hasType Rule</td>
<td>Identifies which AST concepts or interfaces have an associated type.</td>
</tr>
</tbody>
</table>
<SectionComponent tag="h2" 
  id="i---n-5"
  bind:intersecting={visibleById["i---n-5"]}
  onIntersectChange={(visible) => setVisible("i---n-5", visible)}
>I - N</SectionComponent>
<table>
<thead>
<tr>
<th>Phrase</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code>if(MetaType)</code> (function)</td>
<td>Limits a scope expression to nodes of a specific metatype (concept or model unit); skips others.</td>
</tr>
<tr>
<td>Imported Nodes</td>
<td>Declared nodes of other namespaces included via an imports block; private properties are excluded.</td>
</tr>
<tr>
<td><code>imports</code> (keyword)</td>
<td>Adds declared nodes from other namespaces to the current namespace’s visible nodes; can be marked recursive.</td>
</tr>
<tr>
<td>Inference Rule</td>
<td>Defines how to determine the type of a term, e.g. infertype self.declaredType.</td>
</tr>
<tr>
<td><code>initialize()</code></td>
<td>Function used in external components to set up and synchronize data before rendering.</td>
</tr>
<tr>
<td>Interpreter</td>
<td>A component that traverses and evaluates the AST to compute values to be associated with certain nodes.</td>
</tr>
<tr>
<td>Interpreter Context</td>
<td>A runtime data structure that stores variable bindings, constants, and references during evaluation, allowing nested evaluations to access local and parent scopes.</td>
</tr>
<tr>
<td>Interpreter Framework</td>
<td>Freon’s subsystem that enables the evaluation of DSL models directly from their Abstract Syntax Tree (AST).</td>
</tr>
<tr>
<td><code>isNamespace</code> (keyword)</td>
<td>Declares which concepts, model units, or interfaces identify namespaces within the language.</td>
</tr>
<tr>
<td><code>istype</code> (keyword)</td>
<td>Declares which AST concepts are recognized as types.</td>
</tr>
<tr>
<td><code>isunique</code> (keyword) / is-unique rule</td>
<td>Built-in validation rule ensuring that all elements in a list are unique, effectively treating the list as a strict set. Used in .valid files to enforce uniqueness constraints within Freon models.</td>
</tr>
<tr>
<td>Language engineer</td>
<td>The person using Freon to create a DSL and the tools to use the DSL.</td>
</tr>
<tr>
<td>Language Environment</td>
<td>Runtime environment containing the generated Fre-tools for a specific language.</td>
</tr>
<tr>
<td>Limited Concept</td>
<td>A structural element in the language, that can only have a limited number of predefined instances. A more extensive version of an enumeration.</td>
</tr>
<tr>
<td>LionWeb</td>
<td>An open initiative for web-based interoperability of modeling tools using a standard JSON interchange format.</td>
</tr>
<tr>
<td>LionWeb repository</td>
<td>A server implementation that stores models using the LionWeb protocol; it can be connected to Freon for model persistence.</td>
</tr>
<tr>
<td>M0 Level</td>
<td>The runtime level, representing values or results from executing or interpreting M1 models.</td>
</tr>
<tr>
<td>M1 Level</td>
<td>The model level, containing user-created instances of language concepts edited in the Freon editor.</td>
</tr>
<tr>
<td>M2 Level</td>
<td>The language definition level, describing available concepts in .ast files.</td>
</tr>
<tr>
<td>Meta Levels</td>
<td>The conceptual separation of model definition (M2), model instance (M1), and runtime execution (M0).</td>
</tr>
<tr>
<td>Metamodel</td>
<td>The structure that defines the syntax and semantics of a DSL in Freon.</td>
</tr>
<tr>
<td>MobX Reactivity</td>
<td>The reactive data layer used in Freon to track model state and automatically update the UI.</td>
</tr>
<tr>
<td>Model</td>
<td>An instance of the language metamodel, like an object is an instance of a class. The model is what the user is interested in.</td>
</tr>
<tr>
<td>Model Unit</td>
<td>A modular part of a model that can be edited or stored separately, enabling large-scale DSL models.</td>
</tr>
<tr>
<td>Name Resolution</td>
<td>Process of linking references in the model to their declared elements.</td>
</tr>
<tr>
<td>Name-based reference</td>
<td>A Freon reference mechanism that identifies model elements by name rather than by internal ID.</td>
</tr>
<tr>
<td>Named Editor</td>
<td>A projection set.</td>
</tr>
<tr>
<td>Named Elements</td>
<td>Model elements with unique names that can be referenced by other parts of the model.</td>
</tr>
<tr>
<td>Named property projection</td>
<td>A single projection in a (named) projection set.</td>
</tr>
<tr>
<td>Namespace</td>
<td>A subtree of the AST where a certain set of nodes is visible, identified by an instance of a concept, model unit, or interface.</td>
</tr>
<tr>
<td>Namespace addition</td>
<td>A rule allowing one namespace to import names from another (e.g., from a related page).</td>
</tr>
<tr>
<td>Namespace Graph / Tree</td>
<td>The overall structure formed by all namespaces; forms a tree when hierarchical, or a directed graph when imports or alternatives exist.</td>
</tr>
<tr>
<td><code>notempty</code> (keyword) / not-empty rule</td>
<td>Built-in validation rule ensuring that lists are not empty. Used in .valid files to enforce that a property contains at least one element within a Freon model.</td>
</tr>
</tbody>
</table>
<SectionComponent tag="h2" 
  id="o---r-6"
  bind:intersecting={visibleById["o---r-6"]}
  onIntersectChange={(visible) => setVisible("o---r-6", visible)}
>O - R</SectionComponent>
<table>
<thead>
<tr>
<th>Phrase</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td>Object Constraint Language (OCL)</td>
<td>A formal language co-created by Jos Warmer for expressing constraints on UML models.</td>
</tr>
<tr>
<td>Operator Priority</td>
<td>The precedence rules defining how binary expressions are grouped during editing and interpretation.</td>
</tr>
<tr>
<td><code>owner()</code> (function)</td>
<td>Returns the owning namespace of the current property or node, used for navigating upward through the AST.</td>
</tr>
<tr>
<td>Parameter Lookup</td>
<td>The process by which the interpreter retrieves a parameter’s current value from the active context.</td>
</tr>
<tr>
<td>Parent Nodes</td>
<td>Visible nodes inherited from the parent namespace, contributing to hierarchical or lexical scoping.</td>
</tr>
<tr>
<td>Pareto principle</td>
<td>The principle that roughly 80% of the effects come from 20% of the causes; used in Freon to explain how the framework focuses on simplifying the majority of language-engineering tasks.</td>
</tr>
<tr>
<td>Parsing (Reader)</td>
<td>The operation of reading a textual or serialized representation into a Freon model.</td>
</tr>
<tr>
<td>Partial Interpretation</td>
<td>Occurs when evaluation cannot be completed because required context or values are missing.</td>
</tr>
<tr>
<td>Precedence of projections</td>
<td>In a set of projections for the same concept, the priority of one projection over the other.</td>
</tr>
<tr>
<td><code>private</code> (keyword)</td>
<td>Marks properties in .ast files that should not be imported into other namespaces.</td>
</tr>
<tr>
<td>Private Properties</td>
<td>Properties in .ast files marked with the <code>private</code> keyword; visible only within their own namespace, not when imported elsewhere.</td>
</tr>
<tr>
<td>Projection</td>
<td>The visual representation of the model’s AST displayed and manipulated in the Freon editor.</td>
</tr>
<tr>
<td>Projection Priority</td>
<td>The Freon mechanism that determines whether a projection comes from a custom, definition-based, or default source.</td>
</tr>
<tr>
<td>Projection set</td>
<td>A named set of projection definitions, one per structural element in the language.</td>
</tr>
<tr>
<td>Projectional Editing</td>
<td>An editing paradigm where users work directly on the model structure (AST) instead of plain text.</td>
</tr>
<tr>
<td>Projectional Editor</td>
<td>An editor that directly manipulates the AST, ensuring models are always syntactically valid.</td>
</tr>
<tr>
<td>Public property</td>
<td>A property that is not declared private with regard to namespace imports.</td>
</tr>
<tr>
<td>Reference</td>
<td>A property linking one concept to another (similar to associations in UML).</td>
</tr>
<tr>
<td>Reference shortcut</td>
<td>A shortcut allowing automatic creation of reference nodes without double selection.</td>
</tr>
<tr>
<td>RenderComponent</td>
<td>Freon’s internal renderer that displays a childBox inside an external component.</td>
</tr>
<tr>
<td>RtArray</td>
<td>Represents arrays of runtime objects.</td>
</tr>
<tr>
<td>RtBoolean</td>
<td>Represents boolean values.</td>
</tr>
<tr>
<td>RtError</td>
<td>Represents runtime errors encountered during interpretation.</td>
</tr>
<tr>
<td>RtNumber</td>
<td>Represents numeric values at runtime.</td>
</tr>
<tr>
<td>RtString</td>
<td>Represents string values.</td>
</tr>
<tr>
<td>Runtime Object (RtObject)</td>
<td>The base class for all runtime results produced by interpretation, ensuring a clear separation between model and runtime layers.</td>
</tr>
<tr>
<td>Runtime Object Library</td>
<td>A collection of classes used for representing evaluated runtime values.</td>
</tr>
</tbody>
</table>
<SectionComponent tag="h2" 
  id="s---t-7"
  bind:intersecting={visibleById["s---t-7"]}
  onIntersectChange={(visible) => setVisible("s---t-7", visible)}
>S - T</SectionComponent>
<table>
<thead>
<tr>
<th>Phrase</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td>Scope provider (or scoper)</td>
<td>A component that is able to determine for each term in the model which names of other elements of the model are visible.</td>
</tr>
<tr>
<td>Scoper</td>
<td>The Freon name for scope provider.</td>
</tr>
<tr>
<td>.scope file</td>
<td>File containing the definition of the scope provider for the language. The extension must be ‘.scope’.</td>
</tr>
<tr>
<td>Scoping</td>
<td>The mechanism that determines which names or elements are visible in a certain context.</td>
</tr>
<tr>
<td>Scoping Rules</td>
<td>Definitions determining which names are visible and resolvable in a given context.</td>
</tr>
<tr>
<td>Scoping, Typing, and Validation</td>
<td>The three main components of Freon’s semantic analysis framework.</td>
</tr>
<tr>
<td><code>self</code> (keyword)</td>
<td>Optional prefix for referencing direct properties within scoping expressions, similar to “this” in programming languages.</td>
</tr>
<tr>
<td>Severity Levels</td>
<td>Possible values for validation message severity: error, warning, hint, improvement, todo, and info.</td>
</tr>
<tr>
<td>Simple Value Rule</td>
<td>Restricts a property to specific values or numeric ranges.</td>
</tr>
<tr>
<td>Singleton Pattern</td>
<td>Ensures only one instance of the standard library (stdlib) exists at runtime.</td>
</tr>
<tr>
<td>Stacked Architecture</td>
<td>Freon’s three-level structure combining default, definition-based, and custom code layers.</td>
</tr>
<tr>
<td>Standard Library (Stdlib)</td>
<td>Collection of built-in elements and reusable constructs defined for a language.</td>
</tr>
<tr>
<td>Svelte Components</td>
<td>UI building blocks written in Svelte; used to extend or replace parts of Freon’s editor interface.</td>
</tr>
<tr>
<td>Symbol</td>
<td>The concrete syntax to be used for the operator in a binary expression. For instance the ’+’ in ‘4+5’.</td>
</tr>
<tr>
<td>Term</td>
<td>Part of a model that can be associated with a type (using a typer), or with a value (using an interpreter).</td>
</tr>
<tr>
<td>Trigger</td>
<td>The key stroke, or series of key strokes, that the user must enter to let the editor perform a certain function.</td>
</tr>
<tr>
<td>Triggers and Key Bindings</td>
<td>User input patterns (keyboard or mouse) that activate specific editor actions.</td>
</tr>
<tr>
<td>Type Check Rule</td>
<td>Verifies that two elements have compatible or identical types (<code>typecheck equalsType</code> or <code>typecheck conformsTo</code>).</td>
</tr>
<tr>
<td>Type Comparison</td>
<td>Type concept instances cannot be compared by identity; comparison must use <code>equalsto</code> or <code>conformsto</code>.</td>
</tr>
<tr>
<td>Type Concept</td>
<td>A specialized concept representing a type within a Freon language. It implements the FreType interface and is not part of the Abstract Syntax Tree (AST) itself but provides the type information associated with model elements or terms.</td>
</tr>
<tr>
<td>Type Conformance</td>
<td>Rules defining whether one type can be used where another is expected.</td>
</tr>
<tr>
<td>Type Equality</td>
<td>Rules defining when two types are considered identical within the type system.</td>
</tr>
<tr>
<td>Type Inference</td>
<td>Process of determining the type of a model element based on context and rules.</td>
</tr>
<tr>
<td>Type provider (or typer)</td>
<td>A component that determines, for each term in the model, which type to associate with that term.</td>
</tr>
<tr>
<td><code>type()</code> (function)</td>
<td>Returns the type of a node as determined by the typer; must appear at the end of an expression.</td>
</tr>
<tr>
<td>Typer</td>
<td>The Freon name for type provider.</td>
</tr>
<tr>
<td>.type file</td>
<td>File containing the definition of the type provider for the language. The extension must be ‘.type’.</td>
</tr>
<tr>
<td>Typing</td>
<td>Determines which elements are valid in specific locations within a model or Abstract Syntax Tree (AST).</td>
</tr>
</tbody>
</table>
<SectionComponent tag="h2" 
  id="u---z-8"
  bind:intersecting={visibleById["u---z-8"]}
  onIntersectChange={(visible) => setVisible("u---z-8", visible)}
>U - Z</SectionComponent>
<table>
<thead>
<tr>
<th>Phrase</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td>Unparsing (Writer)</td>
<td>The reverse process of serializing a model from AST to text.</td>
</tr>
<tr>
<td>User</td>
<td>The person using Freon’s tools — e.g., the editor, scoper, or validator — to create models in a DSL.</td>
</tr>
<tr>
<td>.valid file</td>
<td>File containing the definition of the validator for the language. The extension must be ‘.valid’.</td>
</tr>
<tr>
<td>Valid Identifier Rule</td>
<td>Checks whether identifiers follow TypeScript’s identifier rules (validIdentifier).</td>
</tr>
<tr>
<td>Validation</td>
<td>Defines the rules that must hold in a language beyond syntax level, ensuring consistency and correctness of the model.</td>
</tr>
<tr>
<td>Validation Rules</td>
<td>Rules that check whether models obey the domain semantics of a language.</td>
</tr>
<tr>
<td>Validator</td>
<td>A component that is able to determine for each term in the model whether the term is correct or erroneous.</td>
</tr>
<tr>
<td>Validator Definition</td>
<td>A .valid file containing the validation rules applied to models built by the user.</td>
</tr>
<tr>
<td>Valid-identifier-rule</td>
<td>A built-in function in the validator definition that defines a check on whether a string is a valid identifier.</td>
</tr>
<tr>
<td>Visible Nodes</td>
<td>The complete set of named nodes accessible in a namespace, constructed from declared, imported, alternative, and parent nodes.</td>
</tr>
<tr>
<td>Where-clause</td>
<td>A part of a typer definition that defines conditions for all properties of a concept. It is used in ‘equalsto’ and ‘conformsto’ definitions.</td>
</tr>
</tbody>
</table>


<PrevNextSection {prevLink} {nextLink} />